import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  ["reference", "Project number", "e.g. 2603"],
  ["name", "Project name", "e.g. 48 Orchard Ave"],
  ["address", "Address (street, suburb, city, postcode)", ""],
  ["client_name", "Client name", ""],
];
const pricingOf = p => p.job_type !== "quick" ? "fixed" : p.pricing === "estimate" ? "estimate" : "chargeup";
const NAMES = { fixed: "Fixed", chargeup: "Charge-up", estimate: "Estimate" };

export default function EditJobDialog({ project, onClose, onSaved }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, project[k] || ""])));
  const start = pricingOf(project);
  const [pricing, setPricing] = useState(start);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const canChange = user?.role === "Admin" && start !== "fixed";

  async function save(e) {
    e.preventDefault();
    if (pricing !== start && !window.confirm(`Change pricing type from ${NAMES[start]} to ${NAMES[pricing]}? Bills, hours and reports are kept.`)) return;
    setBusy(true); setErr("");
    try {
      await api.patch(`/projects/${project.id}`, form);
      if (pricing !== start) {
        if (start === "chargeup" && pricing === "fixed") await api.post(`/projects/${project.id}/convert-to-full`);
        else await api.post(`/projects/${project.id}/pricing`, { to: pricing });
        if (pricing === "fixed") nav(`/projects/${project.id}/estimate`);
      }
      onSaved();
    }
    catch (ex) { setErr(ex?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="edit-job-dialog">
      <form onSubmit={save} className="card w-full max-w-md space-y-3">
        <h2 className="text-silver text-lg">Edit project details</h2>
        {FIELDS.map(([k, label, ph]) => (
          <label key={k} className="block text-sm">{label}
            <input className="input w-full mt-1" placeholder={ph} value={form[k]}
              onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} data-testid={`edit-job-${k}`} />
          </label>
        ))}
        <label className="block text-sm">Pricing type
          <select className="input w-full mt-1" value={pricing} disabled={!canChange} onChange={e => setPricing(e.target.value)} data-testid="edit-job-pricing">
            {Object.entries(NAMES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        {pricing === "fixed" && start !== "fixed" && <div className="text-xs text-sky-300">Fixed opens the quote. You build and accept the quote to set the contract value.</div>}
        <div className="text-xs opacity-60">{start === "estimate" ? "Edit the estimated value on the Estimate card." : "Contract value comes from the accepted quote and isn't edited here."}</div>
        {err && <div className="text-red-400 text-sm" data-testid="edit-job-error">{err}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="edit-job-cancel">Cancel</button>
          <button className="btn-silver" disabled={busy} data-testid="edit-job-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
